import { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, onValue, set } from 'firebase/database';
import { DEFAULT_CATEGORIES } from '../constants/index';
import { DEFAULT_RUNWAY_FLOOR } from '../lib/forecasting/runway';
import { CONNECTIONS_NODE } from '../lib/dbKeys';
import { dedupeTransactions } from '../lib/dedupeTransactions';

/**
 * Subscribes to all Firebase Realtime Database paths for a given user and
 * returns the live data state. Cleans up all listeners when `uid` changes or
 * the component unmounts.
 *
 * @param {string|null} uid - Firebase Auth user ID, or null when signed out.
 * @returns {object} All data state variables and their setters.
 */
export function useFirebaseData(uid) {
  const [transactions, setTx]               = useState([]);
  const [envelopes, setEnvelopes]           = useState({});
  const [envelopeMeta, setEnvelopeMeta]     = useState({});
  const [goals, setGoals]                   = useState([]);
  const [scenarios, setScenarios]           = useState([]);
  const [plannedSeries, setPlannedSeries]   = useState([]);
  const [seriesLifecycle, setSeriesLifecycle] = useState({});
  const [recurringDismissed, setRecurringDismissed] = useState({});
  const [seriesNames, setSeriesNames]       = useState({});
  const [appCategories, setAppCategories]   = useState(DEFAULT_CATEGORIES);
  const [appCategoryMeta, setAppCategoryMeta] = useState({});
  const [accounts, setAccounts]             = useState([]);
  const [bankConnections, setBankConnections] = useState([]);
  // Low-balance floor for the cash runway. Synced (not device-local) because a
  // financial threshold should read the same on web and iOS.
  const [runwayFloor, setRunwayFloor] = useState(DEFAULT_RUNWAY_FLOOR);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [configLoaded, setConfigLoaded]     = useState(false);
  const [dataReady, setDataReady]           = useState(false);

  useEffect(() => {
    if (!uid) {
      // Reset all state when the user signs out.
      setTx([]);
      setEnvelopes({});
      setEnvelopeMeta({});
      setGoals([]);
      setScenarios([]);
      setPlannedSeries([]);
      setSeriesLifecycle({});
      setRecurringDismissed({});
      setSeriesNames({});
      setAppCategories(DEFAULT_CATEGORIES);
      setAppCategoryMeta({});
      setAccounts([]);
      setBankConnections([]);
      setProfilePhotoUrl(null);
      setConfigLoaded(false);
      setDataReady(false);
      return;
    }

    setDataReady(false);
    let firstTxSnap = false;

    const catUnsub = onValue(ref(database, `users/${uid}/categories`), snap => {
      setAppCategories(snap.val() || DEFAULT_CATEGORIES);
    });

    const catMetaUnsub = onValue(ref(database, `users/${uid}/categoryMeta`), snap => {
      const raw = snap.val() ?? {};
      const norm = {};
      Object.entries(raw).forEach(([k, v]) => {
        if (v && typeof v === 'object' && (v.icon || v.tint)) {
          norm[k] = { ...(v.icon ? { icon: v.icon } : {}), ...(v.tint ? { tint: v.tint } : {}) };
        }
      });
      setAppCategoryMeta(norm);
    });

    const txUnsub = onValue(ref(database, `users/${uid}/transactions`), snap => {
      const d = snap.val();
      setTx(
        d
          ? dedupeTransactions(
              Object.entries(d)
                .map(([id, tx]) => ({
                  id,
                  ...tx,
                  amount: typeof tx.amount === 'number' && !Number.isNaN(tx.amount) ? tx.amount : Number(tx.amount) || 0,
                }))
                // Sort BEFORE deduping so the surviving copy holds a position
                // that is already correct for the sorted list.
                .sort((a, b) => new Date(b.date) - new Date(a.date))
            )
          : []
      );
      if (!firstTxSnap) { firstTxSnap = true; setDataReady(true); }
    });

    const envUnsub = onValue(ref(database, `users/${uid}/envelopes`), snap => {
      const raw = snap.val() ?? {};
      setEnvelopes(
        Object.fromEntries(
          Object.entries(raw).map(([k, v]) => [
            k,
            typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0,
          ])
        )
      );
    });

    const envMetaUnsub = onValue(ref(database, `users/${uid}/envelopeMeta`), snap => {
      const raw = snap.val() ?? {};
      const norm = {};
      Object.entries(raw).forEach(([k, v]) => {
        if (v && typeof v === 'object') norm[k] = { rollover: !!v.rollover };
      });
      setEnvelopeMeta(norm);
    });

    const recurDismissUnsub = onValue(ref(database, `users/${uid}/recurringDismissed`), snap => {
      setRecurringDismissed(snap.val() || {});
    });

    // Custom display names for recurring series, keyed by firebaseSafeKey(seriesKey).
    // Kept separate from transaction descriptions so renaming a series never
    // changes its matching identity.
    const seriesNamesUnsub = onValue(ref(database, `users/${uid}/seriesNames`), snap => {
      setSeriesNames(snap.val() || {});
    });

    const scenariosUnsub = onValue(ref(database, `users/${uid}/scenarios`), snap => {
      const raw = snap.val() ?? {};
      setScenarios(
        Object.entries(raw).map(([id, s]) => ({
          id,
          name: s.name || '',
          enabled: !!s.enabled,
          deltas: Array.isArray(s.deltas) ? s.deltas : [],
        }))
      );
    });

    // Recurring commitments entered before their first charge exists. Kept out
    // of `transactions` on purpose: they feed the projectors, never the ledger.
    const plannedUnsub = onValue(ref(database, `users/${uid}/plannedSeries`), snap => {
      const raw = snap.val() ?? {};
      setPlannedSeries(
        Object.entries(raw).map(([id, p]) => ({
          id,
          name: p.name || '',
          amount: Number(p.amount) || 0,
          type: p.type === 'income' ? 'income' : 'expense',
          recurrenceType: p.recurrenceType || 'monthly',
          firstDate: p.firstDate || null,
          category: p.category || 'Other',
          accountId: p.accountId || null,
          matchHint: p.matchHint || null,
          status: p.status === 'linked' ? 'linked' : 'active',
          linkedKey: p.linkedKey || null,
          // Transactions the user said aren't this plan — never suggested again.
          dismissedTxIds: p.dismissedTxIds && typeof p.dismissedTxIds === 'object' ? p.dismissedTxIds : {},
        }))
      );
    });

    // The user's answers about recurring series whose charge didn't arrive —
    // skipped cycles, series that ended, descriptor aliases. See missed.js.
    // A separate node from seriesOverrides on purpose: iOS writes that record
    // whole, and would erase any field it doesn't know about.
    const lifecycleUnsub = onValue(ref(database, `users/${uid}/seriesLifecycle`), snap => {
      setSeriesLifecycle(snap.val() || {});
    });

    const goalsUnsub = onValue(ref(database, `users/${uid}/goals`), snap => {
      const raw = snap.val() ?? {};
      setGoals(
        Object.entries(raw)
          .map(([id, g]) => ({
            id,
            ...g,
            target: Number(g.target) || 0,
            startingAmount: Number(g.startingAmount) || 0,
          }))
          .sort((a, b) => {
            const ad = a.targetDate || '';
            const bd = b.targetDate || '';
            return ad.localeCompare(bd);
          })
      );
    });

    const accountsUnsub = onValue(ref(database, `users/${uid}/accounts`), snap => {
      const d = snap.val();
      setAccounts(d ? Object.entries(d).map(([id, a]) => ({ id, ...a })) : []);
    });

    const connectionsUnsub = onValue(ref(database, `users/${uid}/${CONNECTIONS_NODE}`), snap => {
      const d = snap.val();
      setBankConnections(d ? Object.entries(d).map(([id, e]) => ({ id, ...e })) : []);
      // Gates auto-sync: signals we've at least tried to load the connections.
      setConfigLoaded(true);
    });

    const runwayFloorUnsub = onValue(ref(database, `users/${uid}/runwayFloor`), snap => {
      const val = Number(snap.val());
      setRunwayFloor(Number.isFinite(val) && val >= 0 ? val : DEFAULT_RUNWAY_FLOOR);
    });

    const profilePhotoUnsub = onValue(ref(database, `users/${uid}/profilePhotoUrl`), snap => {
      setProfilePhotoUrl(snap.val() || null);
    });

    // Record the device's IANA zone. Transactions are stored as ISO instants
    // pinned to local midnight, so the server-side sync (api/lib/syncPipeline.js)
    // needs to know which zone "local" means — it has no browser to ask.
    const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (deviceZone) set(ref(database, `users/${uid}/timezone`), deviceZone);

    return () => {
      catUnsub();
      catMetaUnsub();
      txUnsub();
      envUnsub();
      envMetaUnsub();
      recurDismissUnsub();
      seriesNamesUnsub();
      scenariosUnsub();
      plannedUnsub();
      lifecycleUnsub();
      goalsUnsub();
      accountsUnsub();
      connectionsUnsub();
      runwayFloorUnsub();
      profilePhotoUnsub();
    };
  }, [uid]);

  return {
    transactions,
    setTx,
    envelopes,
    setEnvelopes,
    envelopeMeta,
    setEnvelopeMeta,
    goals,
    setGoals,
    scenarios,
    setScenarios,
    plannedSeries,
    setPlannedSeries,
    seriesLifecycle,
    setSeriesLifecycle,
    recurringDismissed,
    setRecurringDismissed,
    seriesNames,
    setSeriesNames,
    appCategories,
    setAppCategories,
    appCategoryMeta,
    setAppCategoryMeta,
    accounts,
    setAccounts,
    bankConnections,
    setBankConnections,
    runwayFloor,
    setRunwayFloor,
    profilePhotoUrl,
    setProfilePhotoUrl,
    configLoaded,
    setConfigLoaded,
    dataReady,
    setDataReady,
  };
}
